import { useEffect, useMemo, useState } from "react";
import { Box, Autocomplete, TextField, styled } from "@mui/material";
import SupplierApi, { Supplier } from "../../../api/ChemInv/Supplier";
import { SupplierForm } from "./SupplierForm";
import { GlobalSecondaryButton } from "../../styles";
import ModalSimpleButton from "../../../components/Modal/ModalSimpleButton";
import { useAuth } from "../../../context/AuthContext";
import { Roles } from "../../../constants/roles";

const initialSupplierData: Supplier = {
  supplierName: "",
  description: "",
  supplierLocation: "",
  certificationDate: null,
  certifiedByEmail: "",
  createdByEmail: "",
  createdDate: "",
  lastModifiedByEmail: null,
  lastModifiedDate: null,
  tradenames: [],
};

const StyledBox = styled(Box)(() => ({
  display: "flex",
  marginBottom: "25px",
}));

const SupplierManagement = () => {
  const [supplierList, setSupplierList] = useState<Supplier[]>([]);
  const [supplierData, setSupplierData] = useState<Supplier>(initialSupplierData);
  const [isNewSupplier, setIsNewSupplier] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [inputValue, setInputValue] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalText, setModalText] = useState("");

  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole([Roles.STLCSME, Roles.Developer]);

  useEffect(() => {
    loadAllSuppliers();
  }, []);

  const loadAllSuppliers = () => {
    setIsLoading(true);
    SupplierApi.getAll().then((res) => {
      setSupplierList(res.sort((a, b) => a.supplierName.localeCompare(b.supplierName)));
      setIsLoading(false);
    });
  };

  const handleSupplierSelection = (supplier: Supplier | null) => {
    setIsNewSupplier(false);
    setSupplierData(initialSupplierData);

    if (supplier?.supplierName) {
      setSupplierData(supplier);
    }
  };

  const handleAddNew = () => {
    setIsNewSupplier(true);
    setSupplierData({ ...initialSupplierData, supplierName: inputValue.trim() });
  };

  const handleCancel = () => {
    setIsNewSupplier(false);
    setSupplierData(initialSupplierData);
    setInputValue("");
  };

  const handleSave = async () => {
    setIsNewSupplier(false);
    setSupplierData(initialSupplierData);
    setInputValue("");
    openModal("Success", "Supplier saved successfully!");
    loadAllSuppliers();
  };

  const handleError = (message: string) => {
    openModal("Save Failed", message);
  };

  const openModal = (title: string, text: string) => {
    setModalTitle(title);
    setModalText(text);
    setModalOpen(true);
  };

  const isNewNameAvailable = useMemo(
    () =>
      inputValue.trim().length > 0 &&
      !supplierList.some(
        (s) => s.supplierName.trim().toLowerCase() === inputValue.trim().toLowerCase(),
      ),
    [inputValue, supplierList],
  );

  const handleInputChange = (
    _e: React.SyntheticEvent,
    newInputValue: string,
    reason: "input" | "reset" | "clear",
  ) => {
    setInputValue(newInputValue);
    if (reason === "clear") {
      setIsNewSupplier(false);
      setSupplierData(initialSupplierData);
    }
    if (reason === "input" && supplierData.supplierName) {
      setSupplierData(initialSupplierData);
      setIsNewSupplier(false);
    }
  };

  const handleAutocompleteChange = (_e: React.SyntheticEvent, value: Supplier | string | null) => {
    if (typeof value === "string") {
      const match = supplierList.find(
        (s) =>
          s.supplierName.trim().toLowerCase() === value.trim().toLowerCase() &&
          s.supplierName !== "",
      );
      if (match) {
        handleSupplierSelection(match);
      } else if (canEdit && isNewNameAvailable) {
        handleAddNew();
      }
    } else {
      handleSupplierSelection(value);
    }
  };

  const filterOptions = (options: Supplier[], state: { inputValue: string }) => {
    if (!state.inputValue) return [];
    const input = state.inputValue.toLowerCase();
    return options.filter((o) => o.supplierName.toLowerCase().includes(input));
  };

  return (
    <StyledBox flexDirection="column" sx={{ pt: 1 }}>
      <StyledBox>
        <Box sx={{ m: 1, display: "flex", flexDirection: "column", gap: 2, width: "44rem" }}>
          <StyledBox sx={{ justifyContent: "space-between" }}>
            <Box sx={{ position: "relative", width: "35rem" }}>
              <Autocomplete
                freeSolo
                noOptionsText={isLoading ? "Loading Suppliers..." : "No Suppliers Found"}
                loading={isLoading}
                id="supplier-select"
                value={supplierData.supplierName ? supplierData : null}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                onChange={handleAutocompleteChange}
                disablePortal
                options={supplierList}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option?.supplierName ?? ""
                }
                filterOptions={filterOptions}
                sx={{ width: "35rem" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    margin="none"
                    label="Supplier Name"
                    placeholder={
                      canEdit
                        ? "Start typing to search or add a supplier..."
                        : "Start typing to search a supplier..."
                    }
                  />
                )}
                isOptionEqualToValue={(option, value) =>
                  value !== null &&
                  (typeof value === "string"
                    ? option.supplierName === value
                    : option.supplierName === value.supplierName)
                }
              />
            </Box>
            {canEdit && (
              <GlobalSecondaryButton
                sx={{ width: "8rem", marginLeft: "1rem" }}
                variant="contained"
                type="button"
                disabled={!isNewNameAvailable}
                onClick={handleAddNew}>
                Add New
              </GlobalSecondaryButton>
            )}
          </StyledBox>
        </Box>
      </StyledBox>

      {(supplierData.supplierName || isNewSupplier) && (
        <SupplierForm
          supplier={isNewSupplier ? { ...initialSupplierData, supplierName: inputValue.trim() } : supplierData}
          isNew={isNewSupplier}
          canEdit={canEdit}
          onSave={handleSave}
          onCancel={handleCancel}
          onError={handleError}
        />
      )}

      <ModalSimpleButton
        title={modalTitle}
        buttonText="Ok"
        buttonAction={() => setModalOpen(false)}
        open={modalOpen}
        setOpen={setModalOpen}>
        <label>{modalText}</label>
      </ModalSimpleButton>
    </StyledBox>
  );
};

export default SupplierManagement;
